import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Dashboard from './components/Dashboard';
import DashboardAssistant from './components/DashboardAssistant';
import CategoryComparison from './components/CategoryComparison';
import TransactionList from './components/TransactionList';
import ReceiptDetail from './components/ReceiptDetail';
import UploadReceipt from './components/UploadReceipt';
import CategoriesManager from './components/CategoriesManager';
import BudgetManager from './components/BudgetManager';
import ShoppingLists from './components/ShoppingLists';
import ShoppingListDetail from './components/ShoppingListDetail';
import UserProfile from './components/UserProfile';
import ExperimentalDictionaryLab from './components/ExperimentalDictionaryLab';
import DemoPage from './pages/DemoPage';
import DemoSignupsPage from './pages/DemoSignupsPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AccountSharingPage from './pages/AccountSharingPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import PrivacyPage from './pages/PrivacyPage';
import { Toaster } from 'react-hot-toast';

const experimentalDictionaryEnabled =
  String(process.env.REACT_APP_ENABLE_EXPERIMENTAL_DICTIONARY || 'false').trim().toLowerCase() === 'true';
const experimentalDictionaryAllowedEnvironment = process.env.NODE_ENV !== 'production';
const showExperimentalDictionary = experimentalDictionaryEnabled && experimentalDictionaryAllowedEnvironment;

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<DemoPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/demo-signups" element={<DemoSignupsPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          
          {/* Protected Routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/assistant"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardAssistant />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/category-comparison"
            element={
              <ProtectedRoute>
                <Layout>
                  <CategoryComparison />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <Layout>
                  <TransactionList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions/receipt/:receiptKey"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReceiptDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions/receipt/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReceiptDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions/receipt/:receiptKey/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReceiptDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <Layout>
                  <UploadReceipt />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <Layout>
                  <CategoriesManager />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/budget"
            element={
              <ProtectedRoute>
                <Layout>
                  <BudgetManager />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/shopping-lists"
            element={
              <ProtectedRoute>
                <Layout>
                  <ShoppingLists />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/shopping-lists/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ShoppingListDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserProfile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/sharing"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountSharingPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {showExperimentalDictionary ? (
            <Route
              path="/experimental/dictionary"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExperimentalDictionaryLab />
                  </Layout>
                </ProtectedRoute>
              }
            />
          ) : null}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
